import {useCallback, useMemo, type CSSProperties, type MouseEvent, type ReactNode} from 'react';
import {
    gridPageCountSelector,
    gridPaginationModelSelector,
    gridPaginationRowCountSelector,
    useGridApiContext,
    useGridRootProps,
    useGridSelector,
} from '@mui/x-data-grid-premium';

export type DataGridPaginationProps = {
    showFirstButton?: boolean;
    showLastButton?: boolean;
    disabled?: boolean;
    className?: string;
    style?: CSSProperties;
    labelRowsPerPage?: ReactNode;
};

const isPageSizeInOptions = (
    pageSize: number,
    pageSizeOptions: readonly (number | {value: number; label: string})[],
): boolean =>
    pageSizeOptions.some(option =>
        typeof option === 'number' ? option === pageSize : option.value === pageSize);

export const DataGridPagination = (props: DataGridPaginationProps) => {
    const apiRef = useGridApiContext();
    const rootProps = useGridRootProps();
    const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
    const rowCount = useGridSelector(apiRef, gridPaginationRowCountSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);
    const {paginationMode, loading} = rootProps;
    const disabled = rowCount === -1 && paginationMode === 'server' && loading;
    const lastPage = useMemo(() => Math.max(0, pageCount - 1), [pageCount]);
    const page = rowCount === -1
        ? paginationModel.page
        : (paginationModel.page <= lastPage ? paginationModel.page : lastPage);
    const pageSizeOptions = isPageSizeInOptions(paginationModel.pageSize, rootProps.pageSizeOptions)
        ? rootProps.pageSizeOptions
        : [];

    const handlePageChange = useCallback((_event: MouseEvent<HTMLButtonElement> | null, nextPage: number) => {
        apiRef.current.setPage(nextPage);
    }, [apiRef]);
    // A new page size reshuffles every page, so the old page index means nothing — go back to the first.
    const handlePageSizeChange = useCallback((pageSize: number) => {
        apiRef.current.setPaginationModel({page: 0, pageSize});
    }, [apiRef]);

    const BasePagination = rootProps.slots.basePagination;

    return (
        <BasePagination
            count={rowCount}
            page={page}
            rowsPerPageOptions={pageSizeOptions}
            rowsPerPage={paginationModel.pageSize}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handlePageSizeChange}
            disabled={disabled}
            style={{maxHeight: 'calc(100% + 1px)', flexGrow: 1}}
            {...{
                showFirstButton: true,
                showLastButton: true,
            }}
            {...rootProps.slotProps?.basePagination}
            {...props}
        />
    );
};
